import React from 'react';

// ── Moon Crescent — cutout circle uses Deep Navy to match splash bg ──────────
function MoonCrescentSplash({ size = 88 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="sp-mg1" cx="38%" cy="32%" r="65%">
          <stop offset="0%"   stopColor="#e8f8ff" />
          <stop offset="45%"  stopColor="#b8e8ff" />
          <stop offset="100%" stopColor="#60c8f0" />
        </radialGradient>
        <filter id="sp-mglow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="sp-msoft" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {/* Outer halo */}
      <circle cx="26" cy="26" r="23" fill="rgba(0,209,255,0.07)" />
      {/* Crescent — full circle minus offset cutout */}
      <g filter="url(#sp-mglow)">
        <circle cx="26" cy="26" r="18" fill="url(#sp-mg1)" />
        {/* Cutout matches splash bg: #0A192F */}
        <circle cx="33" cy="22" r="14" fill="#0A192F" />
      </g>
      {/* Rim */}
      <circle cx="26" cy="26" r="18" fill="none"
        stroke="rgba(180,240,255,0.22)" strokeWidth="0.8" filter="url(#sp-msoft)" />
      {/* Glint */}
      <ellipse cx="19" cy="20" rx="4" ry="2.5" fill="rgba(255,255,255,0.18)"
        style={{ transform: 'rotate(-30deg)', transformOrigin: '19px 20px' }} />
    </svg>
  );
}

// ── Splash Screen ─────────────────────────────────────────────────────────────
interface SplashScreenProps {
  /** Khi true: bắt đầu fade-out (opacity → 0) */
  fading?: boolean;
  message?: string;
}

export function SplashScreen({
  fading  = false,
  message = 'Đang đồng bộ hóa không gian gia sản...',
}: SplashScreenProps) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#0A192F',
      opacity: fading ? 0 : 1,
      transition: 'opacity 0.55s ease',
      pointerEvents: fading ? 'none' : 'all',
    }}>

      {/* Subtle ocean depth glow */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 45% at 50% 58%, rgba(21,80,128,0.45) 0%, transparent 70%)',
      }} />

      {/* Star particles */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        {[
          { x: 15, y: 18, r: 0.9, d: 0    },
          { x: 72, y: 12, r: 1.1, d: 0.8  },
          { x: 40, y: 8,  r: 0.7, d: 1.6  },
          { x: 88, y: 25, r: 0.8, d: 0.4  },
          { x: 25, y: 35, r: 1.2, d: 2.2  },
          { x: 60, y: 22, r: 0.6, d: 1.0  },
          { x: 82, y: 42, r: 0.9, d: 1.8  },
          { x: 10, y: 55, r: 0.7, d: 0.6  },
          { x: 50, y: 15, r: 1.0, d: 2.8  },
          { x: 93, y: 65, r: 0.8, d: 1.4  },
        ].map((s, i) => (
          <circle key={i} cx={`${s.x}%`} cy={`${s.y}%`} r={s.r}
            fill="rgba(200,228,255,0.65)"
            style={{ animation: `sp-twinkle 3.2s ease-in-out ${s.d}s infinite` }} />
        ))}
      </svg>

      {/* Moon logo — pulsing glow */}
      <div style={{
        animation: 'sp-pulse 2.6s ease-in-out infinite',
        position: 'relative', zIndex: 1,
      }}>
        <MoonCrescentSplash size={96} />
      </div>

      {/* App name */}
      <div style={{
        marginTop: 24, fontSize: 20, fontWeight: 700, letterSpacing: '0.12em',
        color: '#C8E4F8', zIndex: 1,
        fontFamily: "'Inter', -apple-system, sans-serif",
        textTransform: 'uppercase',
      }}>
        MoBu
      </div>

      {/* Message */}
      <div style={{
        marginTop: 10, fontSize: 12.5, color: 'rgba(160,200,235,0.65)',
        letterSpacing: '0.04em', zIndex: 1,
        fontFamily: "'Inter', -apple-system, sans-serif",
        whiteSpace: 'nowrap', textAlign: 'center', lineHeight: 1.5,
      }}>
        {message}
      </div>

      {/* Animated dots */}
      <div style={{ display: 'flex', gap: 7, marginTop: 28, zIndex: 1 }}>
        {[0, 0.28, 0.56].map((delay, i) => (
          <div key={i} style={{
            width: 5, height: 5, borderRadius: '50%',
            backgroundColor: '#00D1FF',
            animation: `sp-dot 1.5s ease-in-out ${delay}s infinite`,
          }} />
        ))}
      </div>

      <style>{`
        @keyframes sp-pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
            filter: drop-shadow(0 0 12px rgba(0,209,255,0.22));
          }
          50% {
            opacity: 0.72;
            transform: scale(0.92);
            filter: drop-shadow(0 0 36px rgba(0,209,255,0.58));
          }
        }
        @keyframes sp-dot {
          0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
          40%           { opacity: 1;   transform: translateY(-6px); }
        }
        @keyframes sp-twinkle {
          0%, 100% { opacity: 0.15; transform: scale(0.8); }
          50%      { opacity: 0.9;  transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
